import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, object, string } from 'yup';

export type CreateProviderFormState = {
  name: string;
  apiUrl: string;
  syncRoundEnd: string | boolean;
  status: string;
  retryScheduledSendCredit: string | boolean;
  refundOnCreditFail: string | boolean;
  filterExternalProviderBets: string | boolean;
  isInternal: string | boolean;
  integrationType: string;
  syncRaindrop: string | boolean;
  syncFreebet: string | boolean;
  notifyOnLastFreebet: string | boolean;
  enableInactivityScreen: string | boolean;
  raindropType: string | null;
};

export const createProviderFormStateDefault: CreateProviderFormState = {
  name: '',
  apiUrl: '',
  syncRoundEnd: '',
  status: '',
  retryScheduledSendCredit: '',
  refundOnCreditFail: '',
  filterExternalProviderBets: '',
  isInternal: '',
  integrationType: '',
  syncRaindrop: '',
  syncFreebet: '',
  notifyOnLastFreebet: '',
  enableInactivityScreen: '',
  raindropType: '',
};

export const createProviderSchemaDefault: ObjectSchema<Partial<CreateProviderFormState>> = object<
  Partial<CreateProviderFormState>
>().shape({
  name: string().required('name is required field'),
  apiUrl: string().required('api url is required field'),
  syncRoundEnd: string().required('sync round end is required field'),
  status: string().required('status round end is required field'),
  retryScheduledSendCredit: string().required('retry scheduled send credit is required field'),
  refundOnCreditFail: string().required('refund on credit fail is required field'),
  filterExternalProviderBets: string().required('filterexternal provider bets is required field'),
  isInternal: string().required('is internal is required field'),
  integrationType: string().required('integration type is required field'),
  syncRaindrop: string().required('sync raindrop is required field'),
  syncFreebet: string().required('sync freebet is required field'),
  notifyOnLastFreebet: string().required('notify on last freebet is required field'),
  enableInactivityScreen: string().required('enable inactivity screen is required field'),
  raindropType: string().required('raindrop type is required field'),
});

type CreateProviderFormContextType = {
  schema: ObjectSchema<Partial<CreateProviderFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateProviderFormState>>>;
  methods?: UseFormReturn<CreateProviderFormState>;
};

const createProviderFormContextDefault = {
  schema: createProviderSchemaDefault,
  setSchema: noop,
} satisfies CreateProviderFormContextType;

export const CreateProviderFormContext = createContext<CreateProviderFormContextType>(
  createProviderFormContextDefault
);

export const useCreateProviderForm = () => {
  const { methods } = useContext(CreateProviderFormContext);
  if (!methods) {
    throw new Error('CreateProviderFormContext is empty');
  }

  return methods;
};

export const useManageCreateProviderSchema = () => {
  const { setSchema } = useContext(CreateProviderFormContext);

  return { setSchema };
};
